import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ArrowTrendingUpIcon, CheckIcon } from '@heroicons/react/20/solid';
import { FlagIcon, ReceiptRefundIcon } from '@heroicons/react/24/outline';
import { FlagIcon as FlagSolidIcon } from '@heroicons/react/24/solid';
import { memo, useCallback, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

const sportsbooks = [
  { id: 1, name: 'Early CRIS', value: 'early_cris' },
  { id: 2, name: 'Early Pinnacle', value: 'early_pinnacle' },
  { id: 3, name: 'Early BetOnline', value: 'early_betonline' },
  { id: 4, name: 'Early Circa', value: 'early_circa' },
];

const known = sportsbooks.map((item) => item.value);

type FlagDropdownProps = {
  id: API.Id;
  flags?: string[];
  updateRequest: any;
};

const FlagDropdown = ({ id, flags = [], updateRequest }: FlagDropdownProps) => {
  const unusedFlags = useMemo(() => flags.filter((str) => !known.includes(str)), [flags]);

  const [loading, setLoading] = useState(false);

  const requestRun = useCallback(
    (body: any) => {
      setLoading(true);
      try {
        const result = updateRequest.run(body, {
          req: { input: `/api/v1/admin/message/update/${id}` },
          dontStoreError: true,
          dontAlterState: true,
        });
        setLoading(false);
        return result;
      } catch (error) {
        setLoading(false);
        throw error;
      }
    },
    [updateRequest, id],
  );

  return (
    <Listbox
      className="flex flex-col-reverse"
      disabled={loading}
      value={flags}
      onChange={(nextFlags) => {
        requestRun({ data: { meta: { flags: nextFlags } } });
      }}
      multiple
    >
      {({ open }) => (
        <div>
          <ListboxButton className="relative p-2 hover:bg-emerald-500/5">
            {flags?.length > 0 ? (
              <FlagSolidIcon
                className="h-4 w-auto shrink-0 text-emerald-500 dark:text-emerald-400"
                aria-hidden="true"
              />
            ) : (
              <FlagIcon
                className="h-4 w-auto shrink-0 text-zinc-600 dark:text-zinc-400"
                aria-hidden="true"
              />
            )}
          </ListboxButton>

          <ListboxOptions
            className="absolute right-20 mt-1 max-h-60 w-auto overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-zinc-900/5 focus:outline-hidden sm:text-sm dark:bg-zinc-900 dark:ring-white/5 transition ease-in duration-100 data-closed:opacity-0 opacity-100"
            transition
          >
            {sportsbooks.map((sportsbook) => (
              <ListboxOption
                key={sportsbook.id}
                className={({ focus }) =>
                  twMerge(
                    focus ? 'bg-emerald-500/80 text-white' : 'text-zinc-900 dark:text-zinc-200',
                    'group/option relative cursor-default select-none py-2 pl-3 pr-9',
                  )
                }
                value={sportsbook.value}
              >
                {({ selected, focus }) => (
                  <>
                    <span className="flex flex-row items-center space-x-2">
                      <ArrowTrendingUpIcon
                        className="h-4 w-auto shrink-0 text-zinc-400 group-hover/option:text-emerald-100 group-active/option:text-emerald-100 dark:text-zinc-600"
                        aria-hidden="true"
                      />
                      <span
                        className={twMerge(
                          selected ? 'font-semibold' : 'font-normal',
                          'block min-w-20 max-w-32 truncate',
                        )}
                      >
                        {sportsbook.name}
                      </span>
                    </span>

                    {selected ? (
                      <span
                        className={twMerge(
                          focus ? 'text-white' : 'text-emerald-500',
                          'absolute inset-y-0 right-0 flex items-center pr-4',
                        )}
                      >
                        <CheckIcon height={20} className="h-5 w-auto shrink-0" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </ListboxOption>
            ))}

            {unusedFlags.map((flag) => (
              <ListboxOption
                key={flag}
                className={({ focus }) =>
                  twMerge(
                    focus ? 'bg-emerald-500/80 text-white' : 'text-zinc-900 dark:text-zinc-200',
                    'group/option relative cursor-default select-none py-2 pr-9 pl-3',
                  )
                }
                value={flag}
              >
                {({ selected, focus }) => (
                  <>
                    <span className="flex flex-row items-center space-x-2">
                      {flag.startsWith('early') ? (
                        <ArrowTrendingUpIcon
                          className="h-4 w-auto shrink-0 text-zinc-400 group-hover/option:text-emerald-100 group-active/option:text-emerald-100 dark:text-zinc-600"
                          aria-hidden="true"
                        />
                      ) : flag.startsWith('otb') ? (
                        <ReceiptRefundIcon
                          className="h-4 w-auto shrink-0 text-zinc-400 group-hover/option:text-emerald-100 group-active/option:text-emerald-100 dark:text-zinc-600"
                          aria-hidden="true"
                        />
                      ) : (
                        <FlagIcon
                          className="h-4 w-auto shrink-0 text-zinc-400 group-hover/option:text-emerald-100 group-active/option:text-emerald-100 dark:text-zinc-600"
                          aria-hidden="true"
                        />
                      )}
                      <span
                        className={twMerge(
                          selected ? 'font-semibold' : 'font-normal',
                          'block min-w-20 max-w-32 truncate',
                        )}
                      >
                        {flag}
                      </span>
                    </span>

                    {selected ? (
                      <span
                        className={twMerge(
                          focus ? 'text-white' : 'text-emerald-500',
                          'absolute inset-y-0 right-0 flex items-center pr-4',
                        )}
                      >
                        <CheckIcon height={20} className="h-5 w-auto shrink-0" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </ListboxOption>
            ))}

            <ListboxOption
              className="group/option pointer-events-none relative cursor-default select-none p-0 text-zinc-900 dark:text-zinc-200"
              value={null}
            >
              <form
                className="pointer-events-auto flex w-full space-x-1 px-1 pt-1"
                action="/release-center"
                method="POST"
                onClick={(event) => {
                  event.stopPropagation();
                }}
                onKeyDown={(event) => {
                  event.stopPropagation();
                }}
                onSubmit={async (event) => {
                  event.preventDefault();
                  event.stopPropagation();

                  if (loading) {
                    return;
                  }

                  const target = event.target as HTMLFormElement;
                  const elements = target.elements;
                  const json = [...elements].reduce(
                    (data, element) => {
                      const el = element as HTMLInputElement;

                      if (el.name && el.value?.trim?.()) {
                        const next = data ? data : {};

                        next[el.name] = el.value.trim().toLowerCase().replace(/ /g, '_');

                        return next;
                      }

                      return data;
                    },
                    null as Record<string, string> | null,
                  );

                  if (json?.name) {
                    await requestRun({ data: { meta: { flags: [...flags, json.name] } } });
                    target.reset();
                  }
                }}
              >
                <input
                  className="m-0 w-32 max-w-full flex-1 shrink border-none bg-white/10 py-2 pr-9 pl-3 text-base sm:text-sm"
                  name="name"
                  placeholder="Custom"
                  autoComplete="off"
                  aria-autocomplete="none"
                />
                <button
                  type="submit"
                  className="flex-1 rounded-sm border-0 bg-emerald-500/20 px-3 py-1 text-sm text-white hover:bg-emerald-600"
                >
                  <span
                    className={twMerge(
                      'inline-block h-4 w-4 leading-4',
                      loading && 'animate-pulse animate-spin',
                    )}
                  >
                    +
                  </span>
                </button>
              </form>
            </ListboxOption>
          </ListboxOptions>
        </div>
      )}
    </Listbox>
  );
};

export default memo(
  FlagDropdown,
  (prevProps, nextProps) =>
    prevProps.id === nextProps.id &&
    prevProps.updateRequest === nextProps.updateRequest &&
    prevProps.flags?.length === nextProps.flags?.length &&
    prevProps.flags?.join?.(',') === nextProps.flags?.join?.(','),
);
